/**
 * Which connector answered last time (app#203), kept in the browser so the next connection asks
 * the right extension first.
 *
 * With two EIP-6963 wallets installed — Rabby and MetaMask, say — the storefront cannot know which
 * one holds the buyer's linked account until one of them hands it over. Once one has, that is the
 * wallet to ask first from then on: per linked address, because a buyer may hold different
 * accounts in different wallets, plus a plain "last used" for the connections that have no linked
 * address to key on (signing in, the Connect button). A wrong memory costs one extra prompt, never
 * a wrong signature — the linked-account check still runs on whatever the remembered wallet hands
 * over.
 *
 * Plain functions over a `Storage`-shaped object, so the decision is testable without a browser;
 * storage failures (private mode, quota) are swallowed — memory is a convenience, not a requirement.
 */

export interface StorageLike {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

// Plain literals, not a template built from a prefix: a module-scope template literal is a
// statement the bundler cannot prove pure, and in the dealer cabinet it kept this whole module in
// the TRON-only build (`check:chains` caught it) — the one build that must carry no EVM code.
const KEY_PREFIX = 'gac.evm.connector:';
const LAST_KEY = 'gac.evm.connector:last';

const keyOf = (linkedAddress?: string): string => (linkedAddress ? KEY_PREFIX + linkedAddress.toLowerCase() : LAST_KEY);

/** The connector to ask first — the one that last matched this address, else the last one used. */
export const rememberedConnectorId = (storage: StorageLike | undefined, linkedAddress?: string): string | undefined => {
  if (!storage) return undefined;

  try {
    return storage.getItem(keyOf(linkedAddress)) ?? storage.getItem(LAST_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

/** Record that this connector handed over the wanted account (or, without one wanted, any account). */
export const rememberConnector = (storage: StorageLike | undefined, connectorId: string, linkedAddress?: string): void => {
  if (!storage) return;

  try {
    storage.setItem(LAST_KEY, connectorId);
    if (linkedAddress) {
      storage.setItem(keyOf(linkedAddress), connectorId);
    }
  } catch {
    // Nothing to do: the next connection simply asks in the default order.
  }
};

/** The browser's storage, or nothing where reading it throws (some privacy modes). */
export const browserStorage = (): StorageLike | undefined => {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
};
